<template>
    <div class="filter-definition-card">
        <div class="card segment-proposal-card">
            <div class="card-body p-3 position-relative">
                <div class="d-flex align-items-start justify-content-between mb-3">
                    <h4 class="h6 fw-semibold mb-0">{{ title }}</h4>
                </div>
                <p class="segment-proposal-match-row mb-2">
                    <span class="me-1">Match</span>
                    <span class="segment-proposal-match-pill mx-1 px-3">{{ matchLabel }}</span>
                    <span class="ms-1">filters:</span>
                </p>
                <div v-if="hasLogicUnits" class="segment-proposal-filter-outer">
                    <template v-for="(group, groupIndex) in orGroups" :key="groupIndex">
                        <div v-if="groupIndex > 0" class="segment-proposal-filter-connector">{{ groupConnectorLabel }}</div>
                        <div class="segment-proposal-filter-wrap">
                            <div v-for="(unit, unitIndex) in group" :key="unitIndex" class="segment-proposal-filter-unit">
                                <span
                                    class="filter-unit-column"
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    :title="formatDbColumn(unit.db_column)"
                                ><span>{{ formatDbColumn(unit.db_column) }}</span></span>
                                <span class="filter-unit-operator mx-3">{{ unit.operator?.text || formatOperator(unit.operator?.value || unit.operator) }}</span>
                                <span v-if="Array.isArray(unit.value) && unit.value.length" class="filter-unit-value">
                                    <template v-for="(val, vi) in unit.value" :key="vi">
                                        <span v-if="showValueAsBadge(unit)" class="filter-unit-value-badge">{{ formatValue(val) }}</span>
                                        <span v-else>{{ formatValue(val) }}</span>
                                    </template>
                                </span>
                                <span v-else-if="unit.value" class="filter-unit-value">
                                    <span v-if="showValueAsBadge(unit)" class="filter-unit-value-badge">{{ formatValue(unit.value) }}</span>
                                    <span v-else>{{ formatValue(unit.value) }}</span>
                                </span>
                                <span v-if="unitIndex < group.length - 1" class="segment-proposal-connector-or-pill">{{ withinGroupConnectorLabel }}</span>
                            </div>
                        </div>
                    </template>
                </div>
                <div v-else class="text-muted mt-2">{{ emptyMessage }}</div>
                <div v-if="hasLogicUnits && contactCount !== undefined" class="segment-proposal-contact-count-wrap mt-3">
                    <div v-if="contactCount !== null && contactCount >= 0" class="segment-proposal-contact-count d-flex align-items-center">
                        <i class="fas fa-users me-2"></i>
                        <span><strong>{{ numberWithZero(contactCount) }} contacts</strong> will be included in this audience.</span>
                    </div>
                    <div v-else-if="contactCount === -1" class="segment-proposal-contact-count d-flex align-items-center text-warning">
                        <i class="fas fa-exclamation-triangle me-2"></i>
                        <span>Unable to calculate contact count.</span>
                    </div>
                    <div v-else class="segment-proposal-contact-count d-flex align-items-center text-muted">
                        <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                        <span>Calculating contact count...</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
    name: 'FilterDefinitionCard',
    data() {
        return {
            /** @type {import('bootstrap').Tooltip[] */
            columnTooltips: [],
        };
    },
    props: {
        /** Filter object with logic_units and filter_logic */
        filter: {
            type: Object,
            default: null,
        },
        /** Optional: pass logic units directly (used when filter.logic_units is the source) */
        logicUnits: {
            type: Array,
            default: null,
        },
        /** Card title */
        title: {
            type: String,
            default: 'Audience Definition',
        },
        /** Show expand icon in header */
        showExpandIcon: {
            type: Boolean,
            default: true,
        },
        /** Contact count to show below filter (null = loading, -1 = error, number = count). Omit to hide block. */
        contactCount: {
            type: Number,
            default: undefined,
        },
        /** Message when no logic units */
        emptyMessage: {
            type: String,
            default: 'No filter definition.',
        },
    },
    computed: {
        hasLogicUnits() {
            const units = this.resolvedLogicUnits;
            return Array.isArray(units) && units.length > 0;
        },
        resolvedLogicUnits() {
            if (this.logicUnits != null) {
                return this.logicUnits;
            }
            return this.filter?.logic_units ?? [];
        },
        /** "All" when top-level is AND, "Any" when top-level is OR (e.g. AI-generated "(1 OR (2 AND 3))"). */
        matchLabel() {
            return this.isInvertedFromLogic(this.filter?.filter_logic) ? 'Any' : 'All';
        },
        /** Between groups: OR when match Any, AND when match All. */
        groupConnectorLabel() {
            return this.isInvertedFromLogic(this.filter?.filter_logic) ? 'OR' : 'AND';
        },
        /** Within a group: AND when match Any, OR when match All. */
        withinGroupConnectorLabel() {
            return this.isInvertedFromLogic(this.filter?.filter_logic) ? 'AND' : 'OR';
        },
        /** Groups parsed from filter_logic (supports nested parens like "(1 OR (2 AND 3))"). */
        orGroups() {
            const units = this.resolvedLogicUnits;
            if (!Array.isArray(units) || units.length === 0) return [];
            if (units.length === 1) return [units];
            const logic = (this.filter?.filter_logic ?? '').trim();
            if (!logic) return [units];
            const inverted = this.isInvertedFromLogic(logic);
            const outerOp = inverted ? 'OR' : 'AND';
            const innerOp = inverted ? 'AND' : 'OR';
            const stripped = logic.replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim();
            const segments = stripped.split(new RegExp(`\\s+${outerOp}\\s+`, 'i')).map(s => s.trim()).filter(Boolean);
            const indexGroups = segments.map(seg =>
                seg.split(new RegExp(`\\s+${innerOp}\\s+`, 'i')).map(s => parseInt(s.trim(), 10) - 1).filter(i => !Number.isNaN(i) && i >= 0 && i < units.length)
            );
            return indexGroups.map(indices => indices.map(i => units[i]).filter(Boolean));
        },
    },
    methods: {
        numberWithZero(num) {
            if (num === null || num === undefined) return '0';
            return Number(num).toLocaleString();
        },
        formatDbColumn(dbColumn) {
            if (!dbColumn) return 'Column';
            const text = dbColumn.text || dbColumn.value || 'Column';
            const category = dbColumn.category;
            return (category != null && category !== '') ? `${category}: ${text}` : text;
        },
        formatOperator(op) {
            if (typeof op === 'object' && op?.text) return op.text;
            const map = { '=': 'Equals', '<>': 'Does Not Equal', 'LIKE': 'Contains', 'NOT LIKE': 'Does Not Contain', '>': '>', '<': '<', '>=': '>=', '<=': '<=', 'IS NULL': 'Is Blank', 'IS NOT NULL': 'Is Not Blank', 'IN': 'Is One Of', 'NOT IN': 'Is Not One Of' };
            return map[op] || op;
        },
        formatValue(val) {
            if (typeof val === 'object' && val !== null) return val.text || val.value || '';
            const str = String(val ?? '');
            const intervalLabel = this.formatBigQueryDateInterval(str);
            if (intervalLabel) return intervalLabel;
            return str;
        },
        /**
         * If value is a BigQuery DATE_SUB/DATE_ADD expression, return "# Unit(s) Ago" or "# Unit(s) From Now".
         * @param {string} value
         * @returns {string|null}
         */
        formatBigQueryDateInterval(value) {
            if (typeof value !== 'string' || !value.trim()) return null;
            const upper = value.trim().toUpperCase();
            const subMatch = upper.match(/DATE_SUB\s*\(\s*CURRENT_DATE\s*\(\s*\)\s*,\s*INTERVAL\s+(\d+)\s+(DAY|MONTH|YEAR)S?\s*\)/i);
            const addMatch = upper.match(/DATE_ADD\s*\(\s*CURRENT_DATE\s*\(\s*\)\s*,\s*INTERVAL\s+(\d+)\s+(DAY|MONTH|YEAR)S?\s*\)/i);
            const n = subMatch ? parseInt(subMatch[1], 10) : addMatch ? parseInt(addMatch[1], 10) : 0;
            const rawUnit = (subMatch && subMatch[2]) || (addMatch && addMatch[2]) || '';
            if (!n || !rawUnit) return null;
            const unit = rawUnit.charAt(0) + rawUnit.slice(1).toLowerCase();
            const plural = n !== 1 ? (unit === 'Day' ? 'Days' : unit + 's') : unit;
            if (subMatch) return `${n} ${plural} Ago`;
            if (addMatch) return `${n} ${plural} From Now`;
            return null;
        },
        /**
         * True when top-level operator in filter_logic is OR (show "Match Any"); false for AND ("Match All").
         * Handles wrapped expressions like "(1 OR (2 AND 3))" by checking operator at parensCount 0 or 1.
         */
        isInvertedFromLogic(logic) {
            if (logic == null) logic = '';
            const stmt = String(logic).toUpperCase().replace(/\(/g, ' ( ').replace(/\)/g, ' ) ');
            const tokens = stmt.match(/[\w()]+|[^\w\s()+-]+/g);
            if (tokens == null || tokens.length <= 1) return false;
            if (/^\d+$/.test(tokens[0])) return tokens[1] === 'OR';
            let parensCount = 0;
            for (let i = 0; i < tokens.length; i++) {
                const token = tokens[i];
                if (token === '(') parensCount++;
                else if (token === ')') parensCount--;
                else if ((parensCount === 0 || parensCount === 1) && (token === 'OR' || token === 'AND'))
                    return token === 'OR';
            }
            return (tokens[2] === 'OR');
        },
        getLogicConnector(index) {
            const logic = this.filter?.filter_logic ?? '';
            const n = index + 1;
            const next = index + 2;
            if (new RegExp(`${n}\\s+AND\\s+${next}`, 'i').test(logic)) return 'AND';
            if (new RegExp(`${n}\\s+OR\\s+${next}`, 'i').test(logic)) return 'OR';
            return 'AND';
        },
        /** Show value as badge only for equals / does not equal; others (e.g. contains) show plain text. */
        showValueAsBadge(unit) {
            const op = unit?.operator?.value ?? unit?.operator;
            return op === '=' || op === '<>';
        },
        initColumnTooltips() {
            this.disposeColumnTooltips();
            if (typeof window.bootstrap === 'undefined') return;
            const els = this.$el?.querySelectorAll?.('.filter-unit-column[data-bs-toggle="tooltip"]');
            if (!els?.length) return;
            this.columnTooltips = [...els].map((el) => new window.bootstrap.Tooltip(el, { container: 'body' }));
        },
        disposeColumnTooltips() {
            this.columnTooltips.forEach((t) => {
                try { t.dispose(); } catch (_) { /* already disposed */ }
            });
            this.columnTooltips = [];
        },
    },
    mounted() {
        this.$nextTick(() => this.initColumnTooltips());
    },
    updated() {
        this.$nextTick(() => this.initColumnTooltips());
    },
    beforeUnmount() {
        this.disposeColumnTooltips();
    },
});
</script>
